import axios from 'axios'
import type { PagedResult } from 'modules/permit/api/contractorService'
import type { PermitListItem } from 'modules/permit/api/permitService'

const DEFAULT_API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? ''
const AUDIT_API_BASE_URL = (process.env.REACT_APP_AUDIT_MIGRATION_API_BASE_URL ?? DEFAULT_API_BASE_URL).replace(/\/$/, '')

const buildUrl = (path: string) => {
  if (!AUDIT_API_BASE_URL) {
    return path
  }
  return `${AUDIT_API_BASE_URL}${path}`
}

type LegacyApiResponse<T> = {
  isSuccess?: boolean
  message?: string | null
  data?: T | null
}

type LegacyPagedData<T> = {
  items?: T[] | null
  currentPage?: number | null
  pageSize?: number | null
  totalPage?: number | null
  totalRecord?: number | null
}

export interface PermitAuditSearchParams {
  permitNo?: string | null
  locationId?: string | null
  auditBy?: string | null
  stDateTime?: string | null
  expDateTime?: string | null
  auditResult?: string | null
  nonconformResult?: string | null
  reviewResult?: string | null
  pageIndex?: number
  pageSize?: number
}

export interface PermitAuditItem {
  auditId: number
  permitNo: string
  workDescr?: string | null
  plant?: string | null
  unit?: string | null
  org?: string | null
  auditByName?: string | null
  auditResult?: string | null
  auditRemark?: string | null
  actionRemark?: string | null
  nonConformResult?: string | null
  reviewResult?: string | null
  auditBy?: string | null
  auditDate?: string | null
  editBy?: string | null
  editDate?: string | null
  dueDate?: string | null
  nonConformRemark?: string | null
  reviewRemark?: string | null
  reviewByName?: string | null
  reviewDate?: string | null
}

export interface NonConformItem {
  nonConFormId: number
  descr: string
  type: string
  workType?: string | null
  itemNo?: string | null
  subitemNo?: string | null
  itemOrder?: number | null
}

export interface PermitAuditDetailItem {
  auditId?: number
  nonconformId: number
  checked: string
  remark?: string | null
  type: string
  descr: string
}

export interface PermitAuditAttachmentItem {
  auditId?: number
  items?: number
  docType: 'Audit' | 'Action'
  docName: string
  docExtension: string
  fileUrl?: string | null
  fileType?: string | null
  visible?: boolean
}

export interface PermitAuditRecord extends PermitAuditItem {
  actionAddBy?: string | null
  actionByName?: string | null
  actionAddDate?: string | null
  reviewBy?: string | null
  detail: PermitAuditDetailItem[]
  attach: PermitAuditAttachmentItem[]
}

export interface PermitAuditPagedResult {
  items: PermitAuditItem[]
  currentPage: number
  pageSize: number
  totalPage: number
  totalRecord: number
}

export interface PermitAuditMasterOption {
  value: string
  label: string
}

export interface AuditEmployeeOption {
  empId: string
  empThName?: string | null
  fullName?: string | null
}

export interface PermitAuditAttachmentUpload {
  docType: 'Audit' | 'Action'
  docName: string
  docExtension: string
  file?: File | null
  fileUrl?: string | null
  fileType?: string | null
}

export interface SavePermitAuditRequest {
  auditId?: number | null
  permitAuditList: string[]
  auditResult: string
  auditRemark: string
  nonConformResult?: string | null
  dueDate?: string | null
  nonConformRemark?: string | null
  actionRemark?: string | null
  reviewResult?: string | null
  reviewRemark?: string | null
  detail: PermitAuditDetailItem[]
  attachments: PermitAuditAttachmentUpload[]
}

export interface PermitAuditPermitLookupQuery {
  tenantId: string
  siteId?: string | null
  companyId?: string | null
  searchTerm?: string | null
  plannedStartFrom?: string | null
  plannedEndTo?: string | null
  pageNumber?: number
  pageSize?: number
}

export interface PermitAuditDeleteRequest {
  auditId: number
}

const ensureSuccess = <T>(response: LegacyApiResponse<T>, fallbackMessage: string) => {
  if (response?.isSuccess === false) {
    throw new Error(response.message ?? fallbackMessage)
  }
}

const normalizeText = (value?: string | null) => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

const normalizeDate = (value?: string | null) => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

const buildSaveFormData = (payload: SavePermitAuditRequest) => {
  const formData = new FormData()
  if (typeof payload.auditId === 'number' && Number.isFinite(payload.auditId)) {
    formData.append('auditId', String(payload.auditId))
  }
  formData.append('permitAuditList', payload.permitAuditList.join(', '))
  formData.append('auditResult', payload.auditResult)
  formData.append('auditRemark', payload.auditRemark)

  const appendIfPresent = (key: string, value?: string | null) => {
    const normalized = normalizeText(value)
    if (normalized) {
      formData.append(key, normalized)
    }
  }

  appendIfPresent('nonConformResult', payload.nonConformResult)
  appendIfPresent('dueDate', normalizeDate(payload.dueDate))
  appendIfPresent('nonConformRemark', payload.nonConformRemark)
  appendIfPresent('actionRemark', payload.actionRemark)
  appendIfPresent('reviewResult', payload.reviewResult)
  appendIfPresent('reviewRemark', payload.reviewRemark)

  formData.append('detailJson', JSON.stringify(payload.detail))
  formData.append(
    'attachmentMetaJson',
    JSON.stringify(
      payload.attachments.map((item) => ({
        docType: item.docType,
        docName: item.docName,
        docExtension: item.docExtension,
        hasNewFile: Boolean(item.file),
        fileUrl: item.fileUrl ?? null,
        fileType: item.fileType ?? null,
      })),
    ),
  )

  payload.attachments.forEach((item) => {
    if (item.file) {
      formData.append('files', item.file, `${item.docName}.${item.docExtension}`)
    }
  })

  return formData
}

export async function searchPermitAudits(params: PermitAuditSearchParams): Promise<PermitAuditPagedResult> {
  const response = await axios.post<LegacyApiResponse<LegacyPagedData<PermitAuditItem>>>(
    buildUrl('/api/v1/PermitAudit'),
    params,
  )

  ensureSuccess(response.data, 'Unable to load permit audit list.')
  const data = response.data?.data

  return {
    items: data?.items ?? [],
    currentPage: data?.currentPage ?? params.pageIndex ?? 1,
    pageSize: data?.pageSize ?? params.pageSize ?? 10,
    totalPage: data?.totalPage ?? 1,
    totalRecord: data?.totalRecord ?? 0,
  }
}

export async function getPermitAuditById(auditId: number | string): Promise<PermitAuditRecord | null> {
  const response = await axios.get<LegacyApiResponse<PermitAuditRecord>>(buildUrl(`/api/v1/PermitAudit/${auditId}`))

  if (response.data?.isSuccess === false) {
    return null
  }

  const data = response.data?.data
  if (!data) {
    return null
  }

  return {
    ...data,
    detail: data.detail ?? [],
    attach: data.attach ?? [],
  }
}

export async function getMasterNonConform(): Promise<NonConformItem[]> {
  const response = await axios.get<LegacyApiResponse<NonConformItem[]>>(buildUrl('/api/v1/Master/non-conform'))
  ensureSuccess(response.data, 'Unable to load non-conform master.')
  return response.data?.data ?? []
}

export async function getMasterLocations(): Promise<PermitAuditMasterOption[]> {
  const response = await axios.get<LegacyApiResponse<PermitAuditMasterOption[]>>(buildUrl('/api/v1/Master/location'))
  ensureSuccess(response.data, 'Unable to load location options.')
  return response.data?.data ?? []
}

export async function getAuditEmployees(): Promise<AuditEmployeeOption[]> {
  const response = await axios.get<LegacyApiResponse<AuditEmployeeOption[]>>(buildUrl('/api/v1/Master/audit-employee'))
  ensureSuccess(response.data, 'Unable to load audit employee options.')
  return response.data?.data ?? []
}

export async function searchAuditPermits(query: PermitAuditPermitLookupQuery): Promise<PagedResult<PermitListItem>> {
  const response = await axios.post<PagedResult<PermitListItem>>(buildUrl('/api/v1/PermitAudit/permits'), query)

  return response.data ?? {
    items: [],
    pageNumber: query.pageNumber ?? 1,
    pageSize: query.pageSize ?? 10,
    totalItems: 0,
    totalPages: 1,
  }
}

export async function validatePermitAudit(payload: { permitAuditList: string[] }): Promise<boolean> {
  const response = await axios.post<LegacyApiResponse<boolean>>(buildUrl('/api/v1/PermitAudit/validate'), {
    permitAuditList: payload.permitAuditList.join(', '),
  })
  ensureSuccess(response.data, 'Unable to validate permit audit.')
  return response.data?.data === true
}

export async function createPermitAudit(payload: SavePermitAuditRequest): Promise<void> {
  const response = await axios.post<LegacyApiResponse<string>>(
    buildUrl('/api/v1/PermitAudit/create'),
    buildSaveFormData(payload),
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  ensureSuccess(response.data, 'Unable to create permit audit.')
}

export async function updatePermitAudit(payload: SavePermitAuditRequest): Promise<void> {
  const response = await axios.post<LegacyApiResponse<string>>(
    buildUrl('/api/v1/PermitAudit/update'),
    buildSaveFormData(payload),
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  ensureSuccess(response.data, 'Unable to update permit audit.')
}

export async function updatePermitAuditReview(payload: {
  auditId: number
  reviewResult: string
  reviewRemark?: string | null
}): Promise<void> {
  const response = await axios.post<LegacyApiResponse<string>>(
    buildUrl('/api/v1/PermitAudit/update-review'),
    payload,
  )
  ensureSuccess(response.data, 'Unable to save permit audit review.')
}

export async function deletePermitAudit(payload: PermitAuditDeleteRequest): Promise<void> {
  const response = await axios.post<LegacyApiResponse<string>>(buildUrl('/api/v1/PermitAudit/delete'), payload)
  ensureSuccess(response.data, 'Unable to delete permit audit.')
}

export async function deletePermitAuditList(payload: { permitAuditList: string[] }): Promise<void> {
  const response = await axios.post<LegacyApiResponse<string>>(buildUrl('/api/v1/PermitAudit/delete-list'), {
    permitAuditList: payload.permitAuditList.join(', '),
  })
  ensureSuccess(response.data, 'Unable to delete duplicate permit audits.')
}
